import { Icons } from '@clientbase/clientbase-library'

import { theme } from 'styles/theme'

/**
 * Vocabulário compartilhado da Central de Notificações (tabela + modal de
 * detalhe). Antes cada tela mantinha a própria cópia deste mapa, com paletas
 * Material soltas (#E8F5E9 / #FFF3E0 / …) que não existem no tema — e que
 * divergiam entre si (`muted_ruler` era #E0E0E0 na tabela e #F5F5F5 no modal).
 * Aqui o status vira um `tone`, e quem renderiza usa o chip canônico
 * (`CellChip`) ou o Alert do MUI, ambos alimentados pelos tokens do tema.
 */
export type NotificationTone =
  | 'success'
  | 'info'
  | 'warning'
  | 'error'
  | 'neutral'

export interface NotificationStatusConfig {
  label: string
  tone: NotificationTone
}

const STATUS_CONFIG: Record<string, NotificationStatusConfig> = {
  sent_to_provider: { label: 'Enviado', tone: 'success' },
  sent_to_recipient: { label: 'Enviado', tone: 'success' },
  delivered: { label: 'Entregue', tone: 'success' },
  opened: { label: 'Aberto', tone: 'info' },
  pending: { label: 'Pendente', tone: 'warning' },
  no_state: { label: 'Pendente', tone: 'warning' },
  cancelled_by_provider: { label: 'Cancelado', tone: 'error' },
  bounced: { label: 'Rejeitado', tone: 'error' },
  undelivered: { label: 'Não entregue', tone: 'error' },
  complaint: { label: 'Spam', tone: 'error' },
  error: { label: 'Erro', tone: 'error' },
  mailbox_full: { label: 'Caixa cheia', tone: 'error' },
  no_email: { label: 'Sem e-mail', tone: 'error' },
  no_phone: { label: 'Sem telefone', tone: 'error' },
  blacklisted: { label: 'Bloqueado', tone: 'error' },
  quota_exceeded: { label: 'Quota excedida', tone: 'error' },
  invalid_phone: { label: 'Telefone inválido', tone: 'error' },
  provider_unavailable: { label: 'Provedor indisponível', tone: 'error' },
  rate_limited: { label: 'Limite de envios atingido', tone: 'error' },
  muted_ruler: { label: 'Silenciado', tone: 'neutral' },
  notifications_disabled: { label: 'Desativado', tone: 'neutral' },
  suppressed_bonified: { label: 'Cobrança bonificada', tone: 'neutral' },
  suppressed_credit_card: { label: 'Cobrança no cartão', tone: 'neutral' }
}

/**
 * `fallbackLabel` é o `status_label` que a API manda — usado quando um status
 * novo chega do backend antes do front conhecer o mapa.
 */
export const getNotificationStatus = (
  status: string,
  fallbackLabel?: string
): NotificationStatusConfig =>
  STATUS_CONFIG[status] || {
    label: fallbackLabel || status,
    tone: 'neutral'
  }

export interface MediumInfo {
  icon: Icons
  color: string
}

// Ícones de canal são monocromáticos: a mesma tinta neutra dos cards de canal
// e dos ícones de modal. O rótulo ao lado ("WhatsApp", "E-mail", "SMS") já
// identifica o canal — cor de marca aqui só criaria três acentos competindo
// dentro da mesma célula/linha.
const MEDIUM_ICON_COLOR = theme.palette.neutral[200]

const MEDIUM_INFO: Record<string, MediumInfo> = {
  'E-mail': { icon: 'faEnvelope', color: MEDIUM_ICON_COLOR },
  SMS: { icon: 'faBell', color: MEDIUM_ICON_COLOR },
  WhatsApp: { icon: 'faWhatsapp', color: MEDIUM_ICON_COLOR }
}

export const getMediumInfo = (medium: string): MediumInfo =>
  MEDIUM_INFO[medium] || {
    icon: 'faBell',
    color: MEDIUM_ICON_COLOR
  }

/**
 * As duas telas recebem o canal em formatos diferentes: o
 * `MessengerTracking::Encoder` manda o rótulo pronto ('E-mail'), enquanto o
 * `CustomerActivityFeedService::Encoder` manda a chave crua de `derive_medium`
 * ('email'). Esta ponte normaliza a chave para o rótulo, de modo que as duas
 * caiam no mesmo `getMediumInfo` em vez de manterem mapas paralelos.
 */
const MEDIUM_KEY_LABELS: Record<string, string> = {
  email: 'E-mail',
  sms: 'SMS',
  whatsapp: 'WhatsApp'
}

export const getMediumLabel = (medium?: string | null): string => {
  if (!medium) return 'Outro'
  return MEDIUM_KEY_LABELS[medium.toLowerCase()] || medium
}

/** `referenceable_type` (model do backend) → rótulo de produto. */
export const REFERENCEABLE_LABELS: Record<string, string> = {
  Billing: 'Cobrança',
  Contract: 'Contrato',
  Customer: 'Cliente',
  Nfse: 'Nota Fiscal',
  Merchant: 'Empresa',
  MerchantUser: 'Usuário',
  CustomerUser: 'Usuário do Cliente',
  Signatory: 'Signatário',
  AnnouncementCustomerPortal: 'Comunicado'
}

export const getReferenceableLabel = (type?: string): string =>
  (type && REFERENCEABLE_LABELS[type]) || type || '-'
